import React, { useState } from 'react';
import EmojiPicker from 'emoji-picker-react';
import { uid } from '../helpers.jsx';
import { C } from '../constants.jsx';
import {
  SH, HR, Row2, Row3, ColorRow, TextInput, NumberInput, Select, TogRow,
  BorderSection, PaddingSection, FBStyleSection, AddButton,
  Collapsible, Slider, Segmented, FontRow, TextStyleSection, ChildMapper,
} from '../panels/PanelPrimitives';

// ─── shared micro-styles ─────────────────────────────────────────────────────
const iSt = {
  display: 'block', width: '100%', padding: '6px 10px', fontSize: 12,
  border: `1px solid ${C.border}`, borderRadius: 7, color: C.text,
  background: C.surface, outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const sel = {
  ...iSt, cursor: 'pointer', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%236B7280'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: 28,
};

function TL({ children }) {
  return (
    <label style={{
      fontSize: 10, fontWeight: 700, color: C.sub, display: 'block',
      marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em',
    }}>
      {children}
    </label>
  );
}

function updArr(arr, i, patch) {
  return arr.map((item, j) => (j === i ? { ...item, ...patch } : item));
}

// ─── STATUS VARIANT PALETTE ──────────────────────────────────────────────────
const STATUS_VARIANTS = [
  ['success', '✓ Success'],
  ['warning', '⚠ Warning'],
  ['danger',  '✕ Danger'],
  ['info',    'ℹ Info'],
  ['neutral', '● Neutral'],
  ['custom',  '✏ Custom'],
];

// ─── TAB STYLE OPTIONS ───────────────────────────────────────────────────────
const TAB_STYLES = [
  ['underline', 'Underline'],
  ['filled',    'Filled'],
  ['pill',      'Pill'],
];

// ─── BREADCRUMB SEPARATORS ───────────────────────────────────────────────────
const SEPARATORS = ['/', '>', '›', '·', '→'];

// ─── ACTION SIZES ────────────────────────────────────────────────────────────
const ACTION_SIZES = [['sm', 'Small'], ['md', 'Medium'], ['lg', 'Large']];

// ─── ACTION VARIANTS ─────────────────────────────────────────────────────────
const ACTION_VARIANTS = [
  ['primary',   'Primary'],
  ['secondary', 'Secondary'],
  ['danger',    'Danger'],
];

const FRONTEND_PUBLIC_ROOT = '/home/demo/frontend/public';
const PAGE_HEADER_ICON_UPLOAD_PATH = `${FRONTEND_PUBLIC_ROOT}/images/logos`;

function toBrowserImageUrl(filePath, fileName = '') {
  const rawValue = String(filePath || fileName || '').trim();
  if (!rawValue) return '';

  if (/^(https?:)?\/\//.test(rawValue) || rawValue.startsWith('data:')) {
    return rawValue;
  }

  const normalized = rawValue.replace(/\\/g, '/');

  if (normalized.startsWith(FRONTEND_PUBLIC_ROOT)) {
    return normalized.slice(FRONTEND_PUBLIC_ROOT.length) || '/';
  }

  const publicIndex = normalized.indexOf('/public/');
  if (publicIndex >= 0) {
    return normalized.slice(publicIndex + '/public'.length) || '/';
  }

  if (normalized.startsWith('/')) {
    return normalized;
  }

  return `/images/logos/${normalized.split('/').pop()}`;
}

function EmojiLibrary({ value, onChange }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginBottom: 12 }}>
      <TextInput
        label="Emoji"
        value={value || ''}
        onChange={onChange}
        placeholder="📋"
      />
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          border: `1px solid ${C.border}`,
          background: C.surface,
          borderRadius: 7,
          padding: '8px 10px',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 12,
          color: C.text,
        }}
      >
        {open ? 'Hide emoji library' : 'Open emoji library'}
      </button>
      {open && (
        <div style={{ marginTop: 8, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <EmojiPicker
            onEmojiClick={(emojiData) => {
              onChange(emojiData.emoji);
              setOpen(false);
            }}
            searchDisabled={false}
            skinTonesDisabled={false}
            previewConfig={{ showPreview: false }}
            lazyLoadEmojis={true}
            width="100%"
          />
        </div>
      )}
    </div>
  );
}

function ImageUploadField({ comp, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filePath', PAGE_HEADER_ICON_UPLOAD_PATH);

    setUploading(true);
    setError('');

    try {
      const response = await fetch('http://154.61.75.209:1200/api/v2/user_modules/upload/', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.Message || 'Upload failed');
      }

      const data = await response.json();
      const iconUrl = toBrowserImageUrl(data.Filepath, data.Filename || file.name);
      onChange({
        iconUrl,
        iconUploadedFileName: data.Filename || file.name,
      });
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <label style={{ fontSize: 12, fontWeight: 500, color: C.text }}>Image upload</label>
        <span style={{ fontSize: 11, color: C.muted }}>Static path</span>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, lineHeight: 1.4, wordBreak: 'break-all' }}>
        {PAGE_HEADER_ICON_UPLOAD_PATH}
      </div>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ ...iSt, padding: 6 }}
      />
      {uploading && <div style={{ marginTop: 6, fontSize: 11, color: C.accent }}>Uploading image...</div>}
      {error && <div style={{ marginTop: 6, fontSize: 11, color: C.red }}>{error}</div>}
      {(comp.iconUploadedFileName || comp.iconUrl) && (
        <div style={{ marginTop: 6, fontSize: 11, color: C.muted, wordBreak: 'break-all' }}>
          {comp.iconUploadedFileName || comp.iconUrl}
        </div>
      )}
      {comp.iconUrl && (
        <div style={{ marginTop: 8 }}>
          <img
            src={comp.iconUrl}
            alt=""
            style={{
              width: 56,
              height: 56,
              objectFit: 'cover',
              borderRadius: comp.iconRadius ?? 8,
              border: `1px solid ${C.border}`,
              background: '#fff',
            }}
          />
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

// ─── BREADCRUMBS EDITOR ──────────────────────────────────────────────────────
function BreadcrumbsEditor({ crumbs, onChange }) {
  const add = () => onChange([...(crumbs || []), { id: uid(), label: 'Page', href: '' }]);
  const del = (i) => onChange((crumbs || []).filter((_, j) => j !== i));
  const upd = (i, patch) => onChange(updArr(crumbs || [], i, patch));

  return (
    <div>
      {(crumbs || []).map((c, i) => (
        <div key={c.id || i} style={{
          display: 'flex', gap: 5, marginBottom: 6, alignItems: 'center',
        }}>
          <input
            style={{ ...iSt, flex: 2 }}
            value={c.label} placeholder="Label"
            onChange={(e) => upd(i, { label: e.target.value })}
          />
          <input
            style={{ ...iSt, flex: 2, fontFamily: 'monospace', fontSize: 11 }}
            value={c.href} placeholder="/path"
            onChange={(e) => upd(i, { href: e.target.value })}
          />
          <button
            onClick={() => del(i)}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.red)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
          >×</button>
        </div>
      ))}
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 6, lineHeight: 1.5 }}>
        Last crumb = current page. Leave href empty for non-links.
      </div>
      <AddButton onClick={add}>Add crumb</AddButton>
    </div>
  );
}

// ─── TABS EDITOR ─────────────────────────────────────────────────────────────
function TabsEditor({ tabs, activeTab, onChange, onActiveChange }) {
  const add = () => {
    const key = `tab${(tabs || []).length + 1}`;
    onChange([...(tabs || []), { id: uid(), label: `Tab ${(tabs || []).length + 1}`, key, icon: '' }]);
  };
  const del = (i) => onChange((tabs || []).filter((_, j) => j !== i));
  const upd = (i, patch) => onChange(updArr(tabs || [], i, patch));

  return (
    <div>
      {(tabs || []).map((t, i) => (
        <div key={t.id || i} style={{
          border: `1px solid ${t.key === activeTab ? C.accentMd : C.border}`,
          borderRadius: 7, marginBottom: 6, overflow: 'hidden',
          background: t.key === activeTab ? C.accentLt : C.surface,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 8px', borderBottom: `1px solid ${C.border}`,
          }}>
            <div
              onClick={() => onActiveChange(t.key)}
              title="Set as active tab"
              style={{
                width: 8, height: 8, borderRadius: '50%',
                background: t.key === activeTab ? C.accent : C.borderMd,
                cursor: 'pointer', flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 11, fontWeight: 600, color: t.key === activeTab ? C.accent : C.sub, flex: 1 }}>
              Tab {i + 1}{t.key === activeTab ? ' · active' : ''}
            </span>
            <button
              onClick={() => del(i)}
              style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, padding: '0 2px', lineHeight: 1 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = C.red)}
              onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
            >×</button>
          </div>
          <div style={{ padding: '8px 8px 4px', display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 5 }}>
            <div>
              <TL>Label</TL>
              <input style={iSt} value={t.label} onChange={(e) => upd(i, { label: e.target.value })} />
            </div>
            <div>
              <TL>Key</TL>
              <input style={{ ...iSt, fontFamily: 'monospace', fontSize: 11 }} value={t.key} onChange={(e) => upd(i, { key: e.target.value })} />
            </div>
            <div>
              <TL>Icon</TL>
              <input style={iSt} value={t.icon} placeholder="emoji" onChange={(e) => upd(i, { icon: e.target.value })} />
            </div>
          </div>
        </div>
      ))}
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>
        Click the dot on a tab to set it as the active preview tab.
      </div>
      <AddButton onClick={add}>Add tab</AddButton>
    </div>
  );
}

// ─── ACTIONS EDITOR (enterprise) ─────────────────────────────────────────────
function ActionsEditor({ actions, onChange }) {
  const add = () => onChange([
    ...(actions || []),
    {
      id: uid(), label: 'Action', icon: '', variant: 'secondary',
      size: 'md', loading: false, disabled: false, disabledReason: '',
      visible: true, requiresConfirm: false, confirmMessage: 'Are you sure?',
      position: 'right', onClick: '',
    },
  ]);
  const del = (i) => onChange((actions || []).filter((_, j) => j !== i));
  const upd = (i, patch) => onChange(updArr(actions || [], i, patch));

  return (
    <div>
      {(actions || []).map((a, i) => (
        <div key={a.id || i} style={{
          border: `1px solid ${C.border}`, borderRadius: 8,
          marginBottom: 8, overflow: 'hidden',
          opacity: a.visible === false ? 0.5 : 1,
        }}>
          {/* Header row */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '7px 10px', background: C.bg,
            borderBottom: `1px solid ${C.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* variant pill */}
              <span style={{
                fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                background: a.variant === 'primary' ? '#2563EB' : a.variant === 'danger' ? '#DC2626' : C.borderMd,
                color: a.variant === 'secondary' ? C.sub : '#fff',
              }}>{a.variant}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{a.label || 'Unnamed'}</span>
              {a.icon && <span style={{ fontSize: 13 }}>{a.icon}</span>}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {/* visible toggle */}
              <span
                onClick={() => upd(i, { visible: !a.visible })}
                title={a.visible ? 'Hide button' : 'Show button'}
                style={{ cursor: 'pointer', fontSize: 14, color: a.visible ? C.accent : C.muted }}
              >{a.visible ? '👁' : '🙈'}</span>
              <button
                onClick={() => del(i)}
                style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.red)}
                onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}
              >×</button>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '10px 10px 6px' }}>
            {/* Label + Icon row */}
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 6, marginBottom: 8 }}>
              <div>
                <TL>Label</TL>
                <input style={iSt} value={a.label} onChange={(e) => upd(i, { label: e.target.value })} />
              </div>
              <div>
                <TL>Icon</TL>
                <input style={iSt} value={a.icon} placeholder="↓" onChange={(e) => upd(i, { icon: e.target.value })} />
              </div>
            </div>

            {/* Variant + Size + Position */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
              <div>
                <TL>Variant</TL>
                <select style={sel} value={a.variant} onChange={(e) => upd(i, { variant: e.target.value })}>
                  {ACTION_VARIANTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <TL>Size</TL>
                <select style={sel} value={a.size || 'md'} onChange={(e) => upd(i, { size: e.target.value })}>
                  {ACTION_SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <TL>Side</TL>
                <select style={sel} value={a.position || 'right'} onChange={(e) => upd(i, { position: e.target.value })}>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>

            {/* States */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: 0, borderTop: `1px solid ${C.border}`, paddingTop: 8,
            }}>
              {/* Loading */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', paddingRight: 10 }}>
                <span style={{ fontSize: 12, color: C.text }}>Loading</span>
                <div
                  onClick={() => upd(i, { loading: !a.loading })}
                  style={{
                    width: 28, height: 16, borderRadius: 8,
                    background: a.loading ? C.accent : C.borderMd,
                    cursor: 'pointer', position: 'relative', transition: 'background .2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2, left: a.loading ? 14 : 2,
                    width: 12, height: 12, borderRadius: '50%', background: '#fff',
                    transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.18)',
                  }} />
                </div>
              </div>
              {/* Disabled */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0' }}>
                <span style={{ fontSize: 12, color: C.text }}>Disabled</span>
                <div
                  onClick={() => upd(i, { disabled: !a.disabled })}
                  style={{
                    width: 28, height: 16, borderRadius: 8,
                    background: a.disabled ? C.accent : C.borderMd,
                    cursor: 'pointer', position: 'relative', transition: 'background .2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2, left: a.disabled ? 14 : 2,
                    width: 12, height: 12, borderRadius: '50%', background: '#fff',
                    transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.18)',
                  }} />
                </div>
              </div>
            </div>

            {/* Disabled reason */}
            {a.disabled && (
              <div style={{ marginTop: 6 }}>
                <TL>Disabled reason (tooltip)</TL>
                <input style={iSt} value={a.disabledReason} placeholder="Explain why disabled…"
                  onChange={(e) => upd(i, { disabledReason: e.target.value })} />
              </div>
            )}

            {/* Confirm */}
            <div style={{ marginTop: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12, color: C.text }}>Requires confirm</span>
                <div
                  onClick={() => upd(i, { requiresConfirm: !a.requiresConfirm })}
                  style={{
                    width: 28, height: 16, borderRadius: 8,
                    background: a.requiresConfirm ? C.accent : C.borderMd,
                    cursor: 'pointer', position: 'relative', transition: 'background .2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2, left: a.requiresConfirm ? 14 : 2,
                    width: 12, height: 12, borderRadius: '50%', background: '#fff',
                    transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.18)',
                  }} />
                </div>
              </div>
              {a.requiresConfirm && (
                <input style={{ ...iSt, marginTop: 5 }} value={a.confirmMessage}
                  placeholder="Are you sure?"
                  onChange={(e) => upd(i, { confirmMessage: e.target.value })} />
              )}
            </div>

            {/* onClick handler */}
            <div style={{ marginTop: 6 }}>
              <TL>onClick handler</TL>
              <input
                style={{ ...iSt, fontFamily: 'monospace', fontSize: 11 }}
                value={a.onClick} placeholder="handleSubmit"
                onChange={(e) => upd(i, { onClick: e.target.value })}
              />
            </div>
          </div>
        </div>
      ))}

      <AddButton onClick={add}>Add action button</AddButton>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PANEL EXPORT
// ═════════════════════════════════════════════════════════════════════════════
export function PageHeaderPanel({ comp, onChange }) {
  return (
    <>
      {/* ── 1. TITLE ────────────────────────────────────────────────── */}
      <Collapsible title="Title" defaultOpen={true}>
        <TextInput
          label="Title" value={comp.title}
          onChange={(v) => onChange({ title: v })}
        />
        <NumberInput
          label="Max width"
          value={comp.titleMaxWidth ?? ''}
          onChange={(v) => onChange({ titleMaxWidth: v })}
          min={0}
          unit="px"
          allowEmpty={true}
          placeholder="Full width"
        />
        <Select
          label="Alignment" value={comp.titleAlign || 'left'}
          onChange={(v) => onChange({ titleAlign: v })}
          options={[['left', 'Left'], ['center', 'Center'], ['right', 'Right']]}
        />
        <TogRow
          label="Wrap text"
          value={!!comp.titleWrap}
          onChange={(v) => onChange({ titleWrap: v })}
        />
        <Row2>
          <Slider label="Line clamp" value={comp.titleLineClamp ?? 1} onChange={(v) => onChange({ titleLineClamp: v })} min={1} max={6} step={1} />
          <Slider label="Line height" value={comp.titleLineHeight ?? 1.2} onChange={(v) => onChange({ titleLineHeight: v })} min={0.8} max={3} step={0.05} />
        </Row2>
        <Slider label="Letter spacing" value={comp.titleLetterSpacing ?? 0} onChange={(v) => onChange({ titleLetterSpacing: v })} min={-2} max={10} step={0.1} unit="px" />
        <Row3>
          <TogRow
            label="Italic"
            value={!!comp.titleItalic}
            onChange={(v) => onChange({ titleItalic: v })}
          />
          <TogRow
            label="Underline"
            value={!!comp.titleUnderline}
            onChange={(v) => onChange({ titleUnderline: v })}
          />
          <Select
            label="Transform"
            value={comp.titleTransform || 'none'}
            onChange={(v) => onChange({ titleTransform: v })}
            options={[['none', 'None'], ['uppercase', 'Uppercase'], ['lowercase', 'Lowercase'], ['capitalize', 'Capitalize']]}
          />
        </Row3>
      </Collapsible>

      <Collapsible title="Title style" defaultOpen={false}>
        <FontRow
          sizeValue={comp.titleFontSize} weightValue={comp.titleFontWeight}
          onSizeChange={(v) => onChange({ titleFontSize: v })}
          onWeightChange={(v) => onChange({ titleFontWeight: v })}
        />
        <ColorRow label="Colour" value={comp.titleColor} onChange={(v) => onChange({ titleColor: v })} />
      </Collapsible>

      {/* ── 2. SUBTITLE ─────────────────────────────────────────────── */}
      <Collapsible title="Subtitle" defaultOpen={false}>
        <TogRow
          label="Show subtitle"
          value={comp.showSubtitle !== false}
          onChange={(v) => onChange({ showSubtitle: v })}
        />
        <TextInput
          label="Subtitle" value={comp.subtitle}
          onChange={(v) => onChange({ subtitle: v })}
          placeholder="Optional subtitle"
        />
        <NumberInput
          label="Max width"
          value={comp.subtitleMaxWidth ?? ''}
          onChange={(v) => onChange({ subtitleMaxWidth: v })}
          min={0}
          unit="px"
          allowEmpty={true}
          placeholder="Full width"
        />
        <Select
          label="Alignment" value={comp.subtitleAlign || 'left'}
          onChange={(v) => onChange({ subtitleAlign: v })}
          options={[['left', 'Left'], ['center', 'Center'], ['right', 'Right']]}
        />
        <TogRow
          label="Wrap text"
          value={comp.subtitleWrap !== false}
          onChange={(v) => onChange({ subtitleWrap: v })}
        />
        <Row2>
          <Slider label="Line clamp" value={comp.subtitleLineClamp ?? 2} onChange={(v) => onChange({ subtitleLineClamp: v })} min={1} max={6} step={1} />
          <Slider label="Line height" value={comp.subtitleLineHeight ?? 1.4} onChange={(v) => onChange({ subtitleLineHeight: v })} min={0.8} max={3} step={0.05} />
        </Row2>
        <Slider label="Letter spacing" value={comp.subtitleLetterSpacing ?? 0} onChange={(v) => onChange({ subtitleLetterSpacing: v })} min={-2} max={10} step={0.1} unit="px" />
        <Row3>
          <TogRow
            label="Italic"
            value={!!comp.subtitleItalic}
            onChange={(v) => onChange({ subtitleItalic: v })}
          />
          <TogRow
            label="Underline"
            value={!!comp.subtitleUnderline}
            onChange={(v) => onChange({ subtitleUnderline: v })}
          />
          <Select
            label="Transform"
            value={comp.subtitleTransform || 'none'}
            onChange={(v) => onChange({ subtitleTransform: v })}
            options={[['none', 'None'], ['uppercase', 'Uppercase'], ['lowercase', 'Lowercase'], ['capitalize', 'Capitalize']]}
          />
        </Row3>
      </Collapsible>

      <Collapsible title="Subtitle style" defaultOpen={false}>
        <FontRow
          sizeValue={comp.subtitleFontSize} weightValue={comp.subtitleFontWeight ?? 400}
          onSizeChange={(v) => onChange({ subtitleFontSize: v })}
          onWeightChange={(v) => onChange({ subtitleFontWeight: v })}
        />
        <ColorRow label="Colour" value={comp.subtitleColor} onChange={(v) => onChange({ subtitleColor: v })} />
      </Collapsible>

      {/* ── 3. ICON / LOGO ──────────────────────────────────────────── */}
      <Collapsible title="Icon / logo" defaultOpen={false}>
        <TogRow
          label="Show icon"
          value={!!comp.showIcon}
          onChange={(v) => onChange({ showIcon: v })}
        />
        {comp.showIcon && (
          <>
            <Segmented
              value={comp.iconType || 'emoji'}
              onChange={(v) => onChange({ iconType: v })}
              options={[['emoji', 'Emoji'], ['url', 'Image upload']]}
            />
            {(!comp.iconType || comp.iconType === 'emoji') ? (
              <EmojiLibrary
                value={comp.iconEmoji}
                onChange={(v) => onChange({ iconEmoji: v })}
              />
            ) : (
              <ImageUploadField
                comp={comp}
                onChange={(patch) => onChange(patch)}
              />
            )}
            <Row2>
              <Slider label="Size" value={comp.iconSize || 32} onChange={(v) => onChange({ iconSize: v })} min={16} max={80} unit="px" />
              <Slider label="Radius" value={comp.iconRadius ?? 8} onChange={(v) => onChange({ iconRadius: v })} min={0} max={40} unit="px" />
            </Row2>
            <ColorRow label="Background" value={comp.iconBg} onChange={(v) => onChange({ iconBg: v })} />
            <Slider label="Gap to title" value={comp.titleAreaGap ?? 10} onChange={(v) => onChange({ titleAreaGap: v })} min={0} max={32} unit="px" />
          </>
        )}
      </Collapsible>

      {/* ── 4. BACK BUTTON ──────────────────────────────────────────── */}
      <Collapsible title="Back button" defaultOpen={false}>
        <TogRow
          label="Show back button"
          value={!!comp.showBack}
          onChange={(v) => onChange({ showBack: v })}
        />
        {comp.showBack && (
          <Row2>
            <TextInput label="Icon" value={comp.backIcon ?? '←'} onChange={(v) => onChange({ backIcon: v })} placeholder="←" />
            <TextInput label="Label" value={comp.backLabel ?? 'Back'} onChange={(v) => onChange({ backLabel: v })} placeholder="Back" />
          </Row2>
        )}
      </Collapsible>

      {/* ── 5. BREADCRUMBS ──────────────────────────────────────────── */}
      <Collapsible title={`Breadcrumbs (${(comp.breadcrumbs || []).length})`} defaultOpen={false}>
        <TogRow
          label="Show breadcrumbs"
          value={!!comp.showBreadcrumbs}
          onChange={(v) => onChange({ showBreadcrumbs: v })}
        />
        {comp.showBreadcrumbs && (
          <>
            <BreadcrumbsEditor
              crumbs={comp.breadcrumbs}
              onChange={(v) => onChange({ breadcrumbs: v })}
            />
            <div style={{ marginTop: 10 }}>
              <TL>Separator</TL>
              <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                {SEPARATORS.map((s) => (
                  <button
                    key={s}
                    onClick={() => onChange({ breadcrumbSeparator: s })}
                    style={{
                      width: 34, height: 28, border: `1px solid ${comp.breadcrumbSeparator === s ? C.accent : C.border}`,
                      borderRadius: 6, background: comp.breadcrumbSeparator === s ? C.accentLt : C.surface,
                      color: comp.breadcrumbSeparator === s ? C.accent : C.sub,
                      cursor: 'pointer', fontSize: 13, fontFamily: 'monospace',
                    }}
                  >{s}</button>
                ))}
              </div>
            </div>
            <Slider label="Font size" value={comp.breadcrumbFontSize ?? 12} onChange={(v) => onChange({ breadcrumbFontSize: v })} min={9} max={18} unit="px" />
            <Row2>
              <ColorRow label="Default colour" value={comp.breadcrumbColor} onChange={(v) => onChange({ breadcrumbColor: v })} />
              <ColorRow label="Active colour" value={comp.breadcrumbActiveColor} onChange={(v) => onChange({ breadcrumbActiveColor: v })} />
            </Row2>
            <ColorRow label="Hover colour" value={comp.breadcrumbHoverColor} onChange={(v) => onChange({ breadcrumbHoverColor: v })} />
          </>
        )}
      </Collapsible>

      {/* ── 6. STATUS BADGE ─────────────────────────────────────────── */}
      <Collapsible title="Status badge" defaultOpen={false}>
        <TogRow
          label="Show status badge"
          value={!!comp.showStatus}
          onChange={(v) => onChange({ showStatus: v })}
        />
        {comp.showStatus && (
          <>
            <TextInput label="Status label" value={comp.statusLabel} onChange={(v) => onChange({ statusLabel: v })} placeholder="Draft" />
            <Select
              label="Variant" value={comp.statusVariant || 'warning'}
              onChange={(v) => onChange({ statusVariant: v })}
              options={STATUS_VARIANTS}
            />
            {comp.statusVariant === 'custom' && (
              <Row2>
                <ColorRow label="Custom bg" value={comp.statusCustomBg} onChange={(v) => onChange({ statusCustomBg: v })} />
                <ColorRow label="Custom text" value={comp.statusCustomColor} onChange={(v) => onChange({ statusCustomColor: v })} />
              </Row2>
            )}
            <Select
              label="Position" value={comp.statusPosition || 'inline'}
              onChange={(v) => onChange({ statusPosition: v })}
              options={[
                ['inline', 'Inline (after title)'],
                ['below-title', 'Below title'],
                ['below-subtitle', 'Below subtitle'],
                ['right', 'Right side'],
                ['top-left', 'Top left'],
                ['top-right', 'Top right'],
              ]}
            />
            <Slider label="Font size" value={comp.statusFontSize ?? 11} onChange={(v) => onChange({ statusFontSize: v })} min={9} max={18} unit="px" />
            <Row2>
              <Slider label="Pad H" value={comp.statusPadH ?? 10} onChange={(v) => onChange({ statusPadH: v })} min={0} max={30} unit="px" />
              <Slider label="Pad V" value={comp.statusPadV ?? 3} onChange={(v) => onChange({ statusPadV: v })} min={0} max={16} unit="px" />
            </Row2>
            <Slider label="Radius" value={comp.statusRadius ?? 20} onChange={(v) => onChange({ statusRadius: v })} min={0} max={40} unit="px" />
          </>
        )}
      </Collapsible>

      {/* ── 7. TAB BAR ──────────────────────────────────────────────── */}
      <Collapsible title={`Tab bar (${(comp.tabs || []).length} tabs)`} defaultOpen={false}>
        <TogRow
          label="Show tab bar"
          value={!!comp.showTabs}
          onChange={(v) => onChange({ showTabs: v })}
        />
        {comp.showTabs && (
          <>
            <Select
              label="Tab style" value={comp.tabStyle || 'underline'}
              onChange={(v) => onChange({ tabStyle: v })}
              options={TAB_STYLES}
            />
            <Row2>
              <ColorRow label="Active colour" value={comp.tabActiveColor} onChange={(v) => onChange({ tabActiveColor: v })} />
              <ColorRow label="Inactive colour" value={comp.tabInactiveColor} onChange={(v) => onChange({ tabInactiveColor: v })} />
            </Row2>
            <ColorRow label="Tab bar background" value={comp.tabBarBg} onChange={(v) => onChange({ tabBarBg: v })} />
            <Row2>
              <Slider label="Font size" value={comp.tabFontSize ?? 13} onChange={(v) => onChange({ tabFontSize: v })} min={10} max={20} unit="px" />
              <Select
                label="Font weight" value={String(comp.tabFontWeight ?? 500)}
                onChange={(v) => onChange({ tabFontWeight: +v })}
                options={[['400', 'Regular'], ['500', 'Medium'], ['600', 'Semibold'], ['700', 'Bold']]}
              />
            </Row2>
            <div style={{ marginTop: 4 }}>
              <TabsEditor
                tabs={comp.tabs}
                activeTab={comp.activeTab}
                onChange={(v) => onChange({ tabs: v })}
                onActiveChange={(key) => onChange({ activeTab: key })}
              />
            </div>
          </>
        )}
      </Collapsible>

      {/* ── 7. METADATA SLOT ────────────────────────────────────────── */}
      <Collapsible title="Right-side metadata" defaultOpen={false}>
        <TogRow
          label="Show metadata text"
          value={!!comp.showMeta}
          onChange={(v) => onChange({ showMeta: v })}
        />
        {comp.showMeta && (
          <>
            <TextInput
              label="Metadata text" value={comp.metaText}
              onChange={(v) => onChange({ metaText: v })}
              placeholder="Record #1042 · Last saved 2m ago"
            />
            <Row2>
              <Slider label="Font size" value={comp.metaFontSize ?? 12} onChange={(v) => onChange({ metaFontSize: v })} min={9} max={18} unit="px" />
              <ColorRow label="Colour" value={comp.metaColor} onChange={(v) => onChange({ metaColor: v })} />
            </Row2>
          </>
        )}
      </Collapsible>

      {/* ── 8. LAYOUT & CONTAINER ───────────────────────────────────── */}
      <Collapsible title="Layout & container" defaultOpen={false}>
        <TogRow
          label="Sticky (position: sticky)"
          value={!!comp.sticky}
          onChange={(v) => onChange({ sticky: v })}
          hint="Sticks to top while scrolling"
        />
        <TogRow
          label="Compact mode"
          value={!!comp.compact}
          onChange={(v) => onChange({ compact: v })}
          hint="Reduces vertical padding"
        />
        <Row2>
          <Slider label="Pad V" value={comp.paddingV ?? 16} onChange={(v) => onChange({ paddingV: v })} min={0} max={60} unit="px" />
          <Slider label="Pad H" value={comp.paddingH ?? 0} onChange={(v) => onChange({ paddingH: v })} min={0} max={60} unit="px" />
        </Row2>
        <NumberInput
          label="Custom height (px)"
          value={comp.customHeight ?? ''}
          onChange={(v) => onChange({ customHeight: v })}
          min={0}
          unit="px"
          allowEmpty={true}
          placeholder="Auto"
        />
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <TogRow label="Bottom border" value={!!comp.borderBottom} onChange={(v) => onChange({ borderBottom: v })} />
        {comp.borderBottom && (
          <ColorRow label="Border colour" value={comp.borderColor} onChange={(v) => onChange({ borderColor: v })} />
        )}
      </Collapsible>

      {/* ── 9. ACTION BUTTONS ───────────────────────────────────────── */}
      <Collapsible title={`Action buttons (${(comp.actions || []).length})`} defaultOpen={true}>
        <Row2>
          <Slider label="Button gap" value={comp.actionGap ?? 6} onChange={(v) => onChange({ actionGap: v })} min={0} max={32} unit="px" />
          <Slider label="Icon/text gap" value={comp.actionInnerGap ?? 5} onChange={(v) => onChange({ actionInnerGap: v })} min={0} max={20} unit="px" />
        </Row2>
        <Row3>
          <NumberInput
            label="Pad H"
            value={comp.actionPadH ?? ''}
            onChange={(v) => onChange({ actionPadH: Number.isNaN(v) ? '' : v })}
            min={0}
            unit="px"
          />
          <NumberInput
            label="Pad V"
            value={comp.actionPadV ?? ''}
            onChange={(v) => onChange({ actionPadV: Number.isNaN(v) ? '' : v })}
            min={0}
            unit="px"
          />
          <NumberInput
            label="Radius"
            value={comp.actionRadius ?? ''}
            onChange={(v) => onChange({ actionRadius: Number.isNaN(v) ? '' : v })}
            min={0}
            unit="px"
          />
        </Row3>
        <ActionsEditor
          actions={comp.actions}
          onChange={(v) => onChange({ actions: v })}
        />
      </Collapsible>
    </>
  );
}
